import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface AgencySummary {
  id: string;
  name: string;
  logoUrl: string | null;
  registeredAt: string | null;
  salesCount: number;
  rentCount: number;
  slug: string | null;
}

export function useAgencySummary(agencyId?: string | null) {
  return useQuery({
    queryKey: ['agency-summary', agencyId],
    enabled: !!agencyId,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<AgencySummary | null> => {
      if (!agencyId) return null;

      const { data: ag } = await supabase
        .from('agencies')
        .select('id, name, logo_url, slug, created_at')
        .eq('id', agencyId)
        .maybeSingle();
      if (!ag) return null;

      const { data: members } = await supabase
        .from('agent_profiles')
        .select('user_id')
        .eq('agency_id', agencyId);
      const userIds = (members || []).map((m: any) => m.user_id).filter(Boolean);

      let salesCount = 0;
      let rentCount = 0;
      if (userIds.length > 0) {
        const [{ count: sc }, { count: rc }] = await Promise.all([
          supabase
            .from('properties')
            .select('id', { count: 'exact', head: true })
            .in('user_id', userIds)
            .eq('status', 'active')
            .eq('approval_status', 'approved')
            .eq('listing_type', 'sale'),
          supabase
            .from('properties')
            .select('id', { count: 'exact', head: true })
            .in('user_id', userIds)
            .eq('status', 'active')
            .eq('approval_status', 'approved')
            .eq('listing_type', 'rent'),
        ]);
        salesCount = sc || 0;
        rentCount = rc || 0;
      }

      return {
        id: ag.id,
        name: ag.name,
        logoUrl: ag.logo_url,
        slug: ag.slug,
        registeredAt: ag.created_at,
        salesCount,
        rentCount,
      };
    },
  });
}
